import { createContext, useContext, useEffect, useRef, useState, useMemo, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { unitPriceFor as computeUnitPrice } from "../lib/menuOptions";

const CartContext = createContext(null);

const STORAGE_KEY = "fff_cart_v1";
// Agency payment preference (see PaymentMethodGate). Persisted across tabs.
// Card/cash only applies once an admin approves it for one order.
const PAYMENT_CHOICE_KEY = "fff_payment_choice_v1";
const DELIVERY_MINIMUM = 400;
// NYC sales tax: 4% state + 4.5% city + 0.375% MCTD = 8.875%.
// Tax-exempt for agency / institutional accounts.
const NYC_SALES_TAX_RATE = 0.08875;

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { items: [], fulfillment: "delivery", deliveryFee: 0, zip: "" };
    const parsed = JSON.parse(raw);
    return {
      items: Array.isArray(parsed.items) ? parsed.items : [],
      fulfillment: parsed.fulfillment === "pickup" ? "pickup" : "delivery",
      deliveryFee: Number(parsed.deliveryFee) || 0,
      zip: parsed.zip || "",
    };
  } catch (err) {
    // localStorage may be blocked (private mode, quota, SSR) — gracefully fall back.
    if (typeof console !== "undefined") console.debug("cart: localStorage read failed", err);
    return { items: [], fulfillment: "delivery", deliveryFee: 0, zip: "" };
  }
}

// Compact identity string used to detect "the buyer context just changed".
// Empty string = anonymous. Role embedded so swapping between private/agency
// also resets the cart even on the same browser.
function authIdentity(user) {
  if (!user || typeof user !== "object") return "";
  return `${user.role || "private"}:${user.id || user.email || ""}`;
}

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [state, setState] = useState(readStored);
  const [menu, setMenu] = useState({ items: [], categories_order: [], role: "private", agency_markup: 1.2, delivery_minimum: DELIVERY_MINIMUM });
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Agency payment method. Agencies default to Purchase Order (agency
  // pricing); card/cash only applies once an admin approves a one-order
  // request. `paymentPref` is what the user wants; `paymentChoice` is what
  // actually applies.
  const [paymentPref, setPaymentPref] = useState(() => {
    try {
      return localStorage.getItem(PAYMENT_CHOICE_KEY) || null;
    } catch {
      return null;
    }
  });
  const [paymentRequest, setPaymentRequest] = useState(null);
  const prevReqStatus = useRef(null);
  const role = user?.role;
  const approvedMethod = paymentRequest?.status === "approved" ? paymentRequest.method : null;
  const paymentChoice =
    role === "agency"
      ? paymentPref && paymentPref === approvedMethod ? paymentPref : "po"
      : role === "admin" ? paymentPref : null;

  const setPaymentChoice = useCallback((v) => {
    setPaymentPref(v);
    try {
      if (v) localStorage.setItem(PAYMENT_CHOICE_KEY, v);
      else localStorage.removeItem(PAYMENT_CHOICE_KEY);
    } catch {
      /* storage disabled — in-memory state still works */
    }
  }, []);

  const refreshPaymentRequest = useCallback(async () => {
    if (role !== "agency") { setPaymentRequest(null); return; }
    try {
      const { data } = await api.get("/agency/payment-method-requests/current");
      const r = data.request;
      // Newly approved (or approved with no saved preference, e.g. new device): switch to it.
      let hasPref = false;
      try { hasPref = !!localStorage.getItem(PAYMENT_CHOICE_KEY); } catch { /* ignore */ }
      if (r?.status === "approved" && (prevReqStatus.current === "pending" || !hasPref)) {
        setPaymentChoice(r.method);
      }
      prevReqStatus.current = r?.status || null;
      setPaymentRequest(r);
    } catch {
      /* non-fatal: stays on PO */
    }
  }, [role, setPaymentChoice]);

  const requestPaymentMethod = useCallback(async (method, note) => {
    const { data } = await api.post("/agency/payment-method-requests", { method, note: note || null });
    setPaymentRequest(data.request);
    prevReqStatus.current = data.request?.status || null;
    setPaymentChoice(method);
    return data.request;
  }, [setPaymentChoice]);

  useEffect(() => { refreshPaymentRequest(); }, [refreshPaymentRequest]);

  // While waiting on the admin, poll so prices switch once approved.
  useEffect(() => {
    if (paymentRequest?.status !== "pending") return undefined;
    const t = setInterval(refreshPaymentRequest, 30000);
    return () => clearInterval(t);
  }, [paymentRequest?.status, refreshPaymentRequest]);

  // Persist
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  // Re-fetch menu whenever auth state changes (role-aware pricing) AND
  // reset cart whenever the buyer context (anon / private / agency / admin)
  // changes — so private items don't carry over into an agency session and
  // vice versa.
  const menuSeq = useRef(0);
  const reloadMenu = useCallback(async () => {
    const seq = ++menuSeq.current;
    try {
      const params = paymentChoice === "po" ? "?pricing=agency" : "?pricing=retail";
      const { data } = await api.get(`/menu${params}`);
      // Ignore stale responses when the payment choice changed mid-flight.
      if (seq === menuSeq.current) setMenu(data);
    } catch (err) {
      // Non-fatal: cart still works with whatever menu we already have.
      if (typeof console !== "undefined") console.debug("cart: menu reload failed", err);
    }
  }, [paymentChoice]);

  const lastIdentityRef = useRef(null);
  useEffect(() => {
    // user === null means we're still resolving the session; do nothing yet.
    if (user === null) return;
    const ident = authIdentity(user);
    const prev = lastIdentityRef.current;
    // Skip the very first resolution (page load) — keep whatever was in
    // localStorage. Only clear on actual transitions.
    if (prev !== null && prev !== ident) {
      setState((s) => ({ ...s, items: [] }));
    }
    lastIdentityRef.current = ident;
    reloadMenu();
  }, [user, reloadMenu]);

  const itemsMap = useMemo(() => {
    const m = {};
    for (const it of menu.items) m[it.id] = it;
    return m;
  }, [menu]);

  // Legacy alias used by older components — base price only.
  const priceMap = useMemo(() => {
    const m = {};
    for (const it of menu.items) m[it.id] = it.price;
    return m;
  }, [menu]);

  // Option-aware unit price (base + sum of selected delta upcharges).
  const unitPriceFor = useCallback(
    (line) => computeUnitPrice(itemsMap[line?.package_id], line),
    [itemsMap]
  );

  const addItem = (pkg_id, qty = 1, selectedOptions = null) => {
    setState((s) => {
      // Lines are considered duplicates only if BOTH package_id and
      // selectedOptions match exactly. Items without options collapse normally.
      const optsKey = JSON.stringify(selectedOptions || null);
      const idx = s.items.findIndex(
        (i) => i.package_id === pkg_id && JSON.stringify(i.selected_options || null) === optsKey
      );
      const items = [...s.items];
      if (idx >= 0) {
        items[idx] = { ...items[idx], quantity: items[idx].quantity + qty };
      } else {
        const line = { package_id: pkg_id, quantity: qty };
        if (selectedOptions) line.selected_options = selectedOptions;
        items.push(line);
      }
      return { ...s, items };
    });
    setDrawerOpen(true);
  };

  const updateQty = (pkg_id, qty, optsKey = undefined) => {
    setState((s) => ({
      ...s,
      items: s.items
        .map((i) => {
          const matches =
            i.package_id === pkg_id &&
            (optsKey === undefined ||
              JSON.stringify(i.selected_options || null) === optsKey);
          return matches ? { ...i, quantity: Math.max(0, qty) } : i;
        })
        .filter((i) => i.quantity > 0),
    }));
  };

  const removeItem = (pkg_id, optsKey = undefined) => {
    setState((s) => ({
      ...s,
      items: s.items.filter((i) => {
        const matches =
          i.package_id === pkg_id &&
          (optsKey === undefined ||
            JSON.stringify(i.selected_options || null) === optsKey);
        return !matches;
      }),
    }));
  };

  const clear = () => setState((s) => ({ ...s, items: [] }));

  // Replace the current cart with a decoded share payload (from ?share=…).
  // Preserves the current fulfillment/zip only when the payload doesn't
  // include them. Called from CartPage on mount when a valid share token
  // is present. See lib/shareCart.js for the payload shape.
  const hydrateFromShare = useCallback((payload) => {
    if (!payload || !Array.isArray(payload.items)) return 0;
    setState((s) => ({
      ...s,
      items: payload.items,
      fulfillment: payload.fulfillment || s.fulfillment,
      zip: payload.zip || s.zip,
      // Delivery fee is re-derived from the ZIP on cart mount, so we clear
      // it here to avoid carrying over a stale value from the sender.
      deliveryFee: 0,
    }));
    return payload.items.length;
  }, []);

  const setFulfillment = (f) => setState((s) => ({ ...s, fulfillment: f }));
  const setDeliveryFee = (fee, zip) =>
    setState((s) => ({ ...s, deliveryFee: Number(fee) || 0, zip: zip ?? s.zip }));

  const subtotal = useMemo(() => {
    return state.items.reduce((sum, i) => sum + unitPriceFor(i) * i.quantity, 0);
  }, [state.items, unitPriceFor]);

  const itemCount = useMemo(
    () => state.items.reduce((s, i) => s + i.quantity, 0),
    [state.items]
  );

  const effectiveDeliveryFee = state.fulfillment === "delivery" ? state.deliveryFee : 0;
  // Tax follows the payment method the agency user picked at the gate.
  // Only PO settlements are tax-exempt (W-9 / resale cert on file); every
  // other flow (CC / cash / private / anon) pays NYC sales tax.
  const isTaxExempt = paymentChoice === "po";
  const taxRate = isTaxExempt ? 0 : NYC_SALES_TAX_RATE;
  // Tax applies only to the food subtotal (delivery is separately stated and
  // therefore non-taxable per NY State Tax Bulletin TB-ST-525).
  const tax = Math.round(subtotal * taxRate * 100) / 100;
  const total = subtotal + effectiveDeliveryFee + tax;
  const minimumMet = state.fulfillment === "pickup" || subtotal >= (menu.delivery_minimum || DELIVERY_MINIMUM);

  // Stable context value so consumers don't re-render unnecessarily.
  const value = useMemo(
    () => ({
      ...state,
      menu,
      priceMap,
      unitPriceFor,
      subtotal,
      deliveryFee: effectiveDeliveryFee,
      tax,
      taxRate,
      isTaxExempt,
      total,
      itemCount,
      minimumMet,
      deliveryMinimum: menu.delivery_minimum || DELIVERY_MINIMUM,
      addItem,
      updateQty,
      removeItem,
      clear,
      hydrateFromShare,
      setFulfillment,
      setDeliveryFee,
      reloadMenu,
      paymentChoice,
      setPaymentChoice,
      paymentRequest,
      requestPaymentMethod,
      refreshPaymentRequest,
      drawerOpen,
      openDrawer: () => setDrawerOpen(true),
      closeDrawer: () => setDrawerOpen(false),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- addItem/updateQty/etc. don't capture stale state; setters are stable.
    [
      state, menu, priceMap, unitPriceFor, subtotal,
      effectiveDeliveryFee, tax, taxRate, isTaxExempt,
      total, itemCount, minimumMet, drawerOpen, reloadMenu,
      paymentChoice, setPaymentChoice,
      paymentRequest, requestPaymentMethod, refreshPaymentRequest,
    ]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
